import { gsap, ScrollTrigger, SplitText } from './gsap-config.js';
import Swiper from 'swiper';
import { Navigation, Parallax, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';

// Cache all DOM elements at initialization
const elements = {
    // Hero section elements
    hero: {
        section      : document.querySelector('[data-anim="hero"]'),
        scrollButton : document.querySelector('[data-anim-role="scroll-button"]'),
        pinHeight    : document.querySelector('[data-anim-role="pin-height"]'),
        heroContainer: document.querySelector('[data-anim-role="heroContainer"]'),
        badge        : document.querySelector('[data-anim-role="badge"]'),
        reflection   : document.querySelector('[data-anim-role="reflection"]'),
        logo         : document.querySelector('[data-anim-role="logo"]'),
        logoContainer: document.querySelector('[data-anim-role="logo-container"]'),
        line1        : document.querySelector('[data-anim-role="line-1"]'),
        line2        : document.querySelector('[data-anim-role="line-2"]')
    },

    // Scrolling photos elements
    photos: {
        container      : document.querySelector('[data-anim="scrolling-photos"]'),
        photosContainer: null, // Will be set conditionally
        photos         : null, // Will be set conditionally
        backgroundIcon : null  // Will be set conditionally
    },

    // Video player elements
    video: {
        playButton    : document.getElementById('play-button'),
        imageContainer: document.querySelector('.image-container'),
        videoContainer: document.getElementById('video-container')
    },

    // Timeline slider elements
    timeline: {
        section      : document.querySelector('[data-slider="dixon-timeline"]'),
        swiperEl     : null, // Will be set conditionally
        prevBtn      : null, // Will be set conditionally
        nextBtn      : null, // Will be set conditionally
        dotsContainer: null, // Will be set conditionally
        labelEl      : null, // Will be set conditionally
        counterEl    : null  // Will be set conditionally
    },

    // Marquee elements
    marquee: {
        rows: document.querySelectorAll('[data-marquee="row"]')
    },
};

// Set conditional elements after main containers are found
if (elements.photos.container) {
    elements.photos.photosContainer = elements.photos.container.querySelector('[data-anim-role="photos-container"]');
    elements.photos.photos          = elements.photos.container.querySelectorAll('[data-anim-role="photo"]');
    elements.photos.backgroundIcon  = elements.photos.container.querySelector('[data-anim-role="background-icon"]');
}

if (elements.timeline.section) {
    elements.timeline.swiperEl      = elements.timeline.section.querySelector('[data-role="timeline-swiper"]');
    // Get both mobile and desktop elements
    elements.timeline.mobileButtons = {
        prevBtn: elements.timeline.section.querySelector('.lg\\:hidden .timeline-prev'),
        nextBtn: elements.timeline.section.querySelector('.lg\\:hidden .timeline-next'),
        counterEl: elements.timeline.section.querySelector('.lg\\:hidden [data-role="slide-counter"]')
    };
    elements.timeline.desktopButtons = {
        prevBtn: elements.timeline.section.querySelector('.hidden.lg\\:block .timeline-prev'),
        nextBtn: elements.timeline.section.querySelector('.hidden.lg\\:block .timeline-next'),
        counterEl: elements.timeline.section.querySelector('.hidden.lg\\:block [data-role="slide-counter"]')
    };
    elements.timeline.dotsContainer = elements.timeline.section.querySelector('[data-role="timeline-dots"]');
    elements.timeline.labelEl       = elements.timeline.section.querySelector('[data-role="timeline-label"]');
}

// Dixon 50 page animations
initHeroAnimations();
initBlobAnimations();
initVideoPlayer();
initScrollingPhotos();
initTimelineSlider();
initGallerySlider();
initMarquee();

// Hero section animations
function initHeroAnimations() {
    const heroElements = elements.hero;
    const heroSection = heroElements.section;

    if (!heroSection) {
        return;
    }

    heroSection.dataset.heroState = 'initializing';

    try {
        const { scrollButton, pinHeight, heroContainer, badge, reflection, logo, logoContainer, line1, line2 } = heroElements;

        if (!badge || !logo || !logoContainer || !line1 || !line2 || !scrollButton) {
            console.warn('Hero animation elements not found');
            return;
        }

        // Get responsive animation values based on screen size
        const getResponsiveValues = () => {
            const isMobile = window.innerWidth < 768; // md breakpoint
            return {
                badgeX: isMobile ? -105 : -198, // Proportional to smaller badge size
                logoX: isMobile ? 50 : 85 // Proportional to smaller logo size
            };
        };

        const animValues = getResponsiveValues();

        const splitLine1 = new SplitText(line1, {
            type: 'words',
            wordsClass: 'word++'
        });

        const splitLine2 = new SplitText(line2, {
            type: 'words',
        });

        // Create main timeline for scroll-based animations
        const mainTimeline = gsap.timeline({
            scrollTrigger: {
                trigger: pinHeight,
                pin: heroContainer,
                start: "top top",
                end: "bottom bottom",
                scrub: 1,
                anticipatePin: 1,
                refreshPriority: 2, // Highest priority for hero pinning
                // markers: true
            }
        });

        // Set initial states for text elements
        gsap.set(logo, {
            autoAlpha: 0,
            filter: 'blur(20px)'
        });

        // Set initial state for reflection (hidden)
        if (reflection) {
            gsap.set(reflection, {
                autoAlpha: 0
            });
        }

        // Set will-change for animated elements
        gsap.set([logoContainer, ...splitLine1.words, ...splitLine2.words], {
            willChange: 'transform, opacity, filter'
        });

        // Build the timeline
        mainTimeline
            // Logo container fade out (opacity only - no force3D needed)
            .to(logoContainer, {
                autoAlpha: 0,
                duration: 1,
                ease: "power3.out"
            })
            // Line 1 fade in (transforms + filter - use force3D)
            .from(splitLine1.words, {
                x: 50,
                filter: 'blur(10px)',
                autoAlpha: 0,
                duration: 1,
                ease: "power3.out",
                force3D: true,
                stagger: {
                    each: 0.02
                },
            })
            // Line 1 fade out (opacity only - no force3D needed)
            .to(splitLine1.words, {
                autoAlpha: 0,
                duration: 0.5,
                ease: "power3.out"
            })
            // Line 2 fade in (transforms + filter - use force3D)
            .from(splitLine2.words, {
                x: 50,
                filter: 'blur(10px)',
                autoAlpha: 0,
                duration: 1,
                ease: "power3.out",
                force3D: true,
                stagger: {
                    each: 0.02
                }
            }); // Start slightly before line1 finishes fading

        // Clean up will-change when timeline completes
        mainTimeline.eventCallback("onComplete", () => {
            gsap.set([logoContainer, ...splitLine1.words, ...splitLine2.words], {
                willChange: 'auto'
            });
        });

        // Separate timeline for badge and logo fade-in on page load (time-based)
        const pageLoadTimeline = gsap.timeline({
            delay: 1,
            onStart: () => {
                // Set will-change for page load animations
                const elementsToAnimate = [badge, logo, scrollButton];
                if (reflection) elementsToAnimate.push(reflection);
                gsap.set(elementsToAnimate, {
                    willChange: 'transform, opacity, filter'
                });
            },
            onComplete: () => {
                // Clean up will-change when page load animations complete
                const elementsToAnimate = [badge, logo, scrollButton];
                if (reflection) elementsToAnimate.push(reflection);
                gsap.set(elementsToAnimate, {
                    willChange: 'auto'
                });
            }
        })
            .from(badge, {
                autoAlpha: 0,
                y: 50,
                filter: 'blur(20px)',
                duration: 3,
                ease: "power3.out",
                force3D: true // Transform + filter - needs GPU
            })
            .to(badge, {
                x: animValues.badgeX,
                duration: 2,
                ease: "power3.inOut",
                force3D: true // Transform - needs GPU
            })
            .to(logo, {
                autoAlpha: 1,
                x: animValues.logoX,
                filter: 'blur(0px)',
                duration: 2,
                ease: "power3.out",
                force3D: true // Transform + filter - needs GPU
            }, "-=1")
            .to(reflection, {
                autoAlpha: 0.1,
                duration: 2,
                ease: "power2.out"
            }, "-=2.5") // Start when logo begins fading in
            .from(scrollButton, {
                autoAlpha: 0,
                y: 10,
                duration: 1,
                ease: "power3.out",
                force3D: true // Transform - needs GPU
            });

        // Handle window resize to update animation values
        let resizeTimeout;
        window.addEventListener('resize', () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                const newValues = getResponsiveValues();
                // Update badge and logo positions if they've moved
                gsap.set(badge, { x: newValues.badgeX });
                gsap.set(logo, { x: newValues.logoX });
            }, 150);
        });
    } catch (error) {
        console.error('Failed to initialize hero animations', error);
    } finally {
        heroSection.dataset.heroState = 'ready';
    }
}

// Blob glow animations
function initBlobAnimations() {
    const { badge } = elements.hero;

    if (!badge) {
        console.warn('Badge element not found for blob animations');
        return;
    }

    // Find all blob elements within the badge
    const blobs = badge.querySelectorAll('.animate-blob');

    if (blobs.length === 0) {
        console.warn('No blob elements found');
        return;
    }

    // Set will-change for blob animations
    gsap.set(blobs, {
        willChange: 'transform, opacity'
    });

    // Clean up will-change after 30 seconds of continuous animation
    gsap.delayedCall(30, () => {
        gsap.set(blobs, {
            willChange: 'auto'
        });
    });

    // Simplified blob animations with reduced complexity
    blobs.forEach((blob, index) => {
        // Combine rotation and scale/opacity into a single timeline for better performance
        const blobTimeline = gsap.timeline({ repeat: -1 });

        blobTimeline
            // Rotation animation
            .to(blob, {
                rotationZ: 360,
                duration: 8 + (index * 3), // Slower, less resource intensive
                ease: "none",
                force3D: true,
                yoyo: true,
                repeat: -1
            }, 0)
            // Simplified scale and opacity (no motion path for better performance)
            .to(blob, {
                scale: 1.1, // Minimal scale change
                opacity: 0.5,
                duration: 3 + (index * 2), // Slower changes
                ease: "sine.inOut",
                yoyo: true,
                repeat: -1
            }, 0);
    });
}

function initScrollingPhotos() {
    const { container, photosContainer: cardsContainer, photos: cards, backgroundIcon } = elements.photos;

    if (!container || !cardsContainer || !cards.length) {
        console.warn('Scrolling photos elements not found');
        return;
    }

    // Calculate the distance to scroll (total width + window width to start completely off-screen)
    const totalDistance = cardsContainer.scrollWidth + window.innerWidth

    // Set initial position off-screen to the right and enable hardware acceleration
    gsap.set(cardsContainer, {
        x: window.innerWidth,
        force3D: true,
        willChange: 'transform'
    });

    // Set will-change for all photo cards
    gsap.set(cards, {
        willChange: 'transform'
    });

    // Clean up will-change when scrolling animation completes
    ScrollTrigger.create({
        trigger: container,
        start: 'top bottom',
        end: () => "+=" + (totalDistance + window.innerHeight),
        onLeave: () => {
            gsap.set([cardsContainer, ...cards], {
                willChange: 'auto'
            });
        }
    });

    // Separate pinning animation - starts later and ends earlier
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const pinTrigger = ScrollTrigger.create({
        trigger: container,
        pin: true,
        anticipatePin: 1,
        start: 'top top',
        end: () => "+=" + (totalDistance * 0.8), // Unpin before photos finish
        refreshPriority: 1 // Higher priority for pinning calculations
    });

    // Photo scrolling animation - starts earlier and continues after unpinning
    const scrollTween = gsap.to(cardsContainer, {
        x: -cardsContainer.scrollWidth,
        ease: 'none',
        force3D: true,
        scrollTrigger: {
            trigger: container,
            scrub: 1,
            start: 'top bottom', // Start when section enters viewport
            end: () => "+=" + totalDistance,
            refreshPriority: 0 // Standard priority for main scroll animation
        }
    });

    cards.forEach(card => {
        const values = {
            x: (Math.random() * 10 + 15) * (Math.random() < 0.5 ? 1 : -1),
            y: (Math.random() * 20 - 10) * (Math.random() < 0.5 ? 1 : -1),
            rotation: (Math.random() * 8 - 4) * (Math.random() < 0.5 ? 1 : -1)
        };

        gsap.fromTo(card, {
            rotation: values.rotation,
            xPercent: values.x,
            yPercent: values.y,
            force3D: true
        }, {
            rotation: -values.rotation,
            xPercent: -values.x,
            yPercent: -values.y,
            ease: 'none',
            force3D: true,
            scrollTrigger: {
                trigger: card,
                containerAnimation: scrollTween,
                start: 'left 120%',
                end: 'right -20%',
                scrub: true,
                refreshPriority: -1 // Lower priority, calculated after main animations
                // markers: true
            }
        })
    })

    // Background icon fade-in and parallax animation
    // if (backgroundIcon) {
    //     // Set initial state - hidden and centered
    //     gsap.set(backgroundIcon, {
    //         autoAlpha: 0,
    //         willChange: 'transform, opacity'
    //     });

    //     // Parallax effect - move up as user scrolls down
    //     gsap.to(backgroundIcon, {
    //         ease: "linear",
    //         autoAlpha: 0.1,
    //         scrollTrigger: {
    //             // markers: true,
    //             trigger: container,
    //             start: 'top bottom',
    //             end: () => "+=" + totalDistance,
    //             scrub: 1, // Slower movement for parallax effect
    //             refreshPriority: -2 // Lower priority
    //         }
    //     });
    // }
}


// Video player functionality
function initVideoPlayer() {
    const { playButton, imageContainer, videoContainer } = elements.video;

    if (!playButton || !imageContainer || !videoContainer) {
        return;
    }

    playButton.addEventListener('click', function() {
        // Get the iframe and add autoplay to the URL
        const iframe = videoContainer.querySelector('iframe');
        if (iframe) {
            const currentSrc = iframe.src;
            // Add autoplay=1 parameter if it's not already there
            if (!currentSrc.includes('autoplay=1')) {
                iframe.src = currentSrc + '&autoplay=1';
            }
        }

        // Set will-change for video transition elements
        gsap.set([playButton, imageContainer, videoContainer], {
            willChange: 'transform, opacity'
        });

        // Hide play button (transform - needs GPU)
        gsap.to(playButton, {
            opacity: 0,
            scale: 0.5,
            duration: 0.5,
            ease: "power3.out",
            force3D: true
        });

        // Fade out image container (opacity only - no force3D needed)
        gsap.to(imageContainer, {
            opacity: 0,
            duration: 0.5,
            ease: "power3.out",
            onComplete: function() {
                // Hide image container completely
                imageContainer.style.visibility = 'hidden';

                // Show video container (opacity only - no force3D needed)
                videoContainer.style.visibility = 'visible';
                videoContainer.classList.remove('invisible', 'opacity-0');
                gsap.to(videoContainer, {
                    opacity: 1,
                    duration: 0.5,
                    ease: "power3.out",
                    onComplete: () => {
                        // Clean up will-change after transition
                        gsap.set([playButton, imageContainer, videoContainer], {
                            willChange: 'auto'
                        });
                    }
                });
            }
        });
    });
}

// Gallery Swiper slider
function initGallerySlider() {
    const gallerySwiper = document.querySelector('[data-tulip-slider] .swiper');
    const paginationEl = document.querySelector('[data-tulip-slider] .swiper-pagination');

    if (!gallerySwiper) {
        console.warn('Gallery swiper element not found');
        return;
    }

    if (!paginationEl) {
        console.warn('Pagination element not found');
        return;
    }

    console.log('Initializing gallery swiper with pagination');

    const swiper = new Swiper(gallerySwiper, {
        modules: [Navigation, Pagination, Autoplay],
        slidesPerView: 1,
        spaceBetween: 20,
        speed: 1000,
        loop: true,
        autoplay: {
            delay: 5000,
            disableOnInteraction: false,
        },
        pagination: {
            el: paginationEl,
            clickable: true,
            type: 'bullets',
            dynamicBullets: false,
            renderBullet: function (index, className) {
                return '<span class="' + className + '"></span>';
            },
        },
        navigation: {
            nextEl: '[data-tulip-slider] .swiper-button-next',
            prevEl: '[data-tulip-slider] .swiper-button-prev',
        },
        breakpoints: {
            768: {
                spaceBetween: 30,
            }
        },
        on: {
            init: function() {
                console.log('Swiper initialized with', this.slides.length, 'slides');
                console.log('Pagination element:', paginationEl);
            }
        }
    });

    console.log('Swiper instance created:', swiper);
}

// Dixon through the Years timeline slider
function initTimelineSlider() {
    const { section, swiperEl, mobileButtons, desktopButtons, dotsContainer, labelEl } = elements.timeline;
    if (!section) return;

    if (!swiperEl || !dotsContainer) return;

    // Determine which buttons to use based on screen size
    const isMobile = window.innerWidth < 1025;
    const activeButtons = isMobile ? mobileButtons : desktopButtons;

    const slides = Array.from(swiperEl.querySelectorAll('.swiper-slide'));

    // Cache slide data calculations - only calculate once
    const slideDataCache = slides.map((slide, index) => {
        const dateAttr = slide.getAttribute('data-year');
        const year = parseInt(dateAttr) || 0;
        const title = slide.getAttribute('data-title') || '';
        const date = slide.getAttribute('data-date') || '';
        return { slide, index, year, title, date };
    });

    // Sort by year to ensure chronological order
    slideDataCache.sort((a, b) => a.year - b.year);

    // Calculate positions based on years - cached calculation
    const minYear = Math.min(...slideDataCache.map(d => d.year));
    const pixelsPerYear = 100;
    const maxPosition = (Math.max(...slideDataCache.map(d => d.year)) - minYear) * pixelsPerYear;

    const dotPositions = slideDataCache.map(data => ({
        ...data,
        position: (data.year - minYear) * pixelsPerYear,
        // Pre-calculate percentage for more efficient positioning
        xPercent: ((data.year - minYear) * pixelsPerYear / maxPosition) * 100
    }));

    // Build timeline dots with chronological positioning
    dotsContainer.innerHTML = '';
    dotsContainer.style.position = 'relative';

    if (isMobile) {
        // Mobile: Allow horizontal overflow and set minimum width
        dotsContainer.style.overflow = 'visible';
        dotsContainer.style.minWidth = `${maxPosition + 100}px`; // Extra space for last dot
    } else {
        // Desktop: Original behavior
        dotsContainer.style.overflow = 'visible';
        dotsContainer.style.minWidth = '';
        // Ensure parent container also allows overflow
        const timelineContainer = dotsContainer.parentElement;
        if (timelineContainer) {
            timelineContainer.style.overflow = 'visible';
        }
    }

    const dots = [];
    dotPositions.forEach((data) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'absolute rounded-full border-4 border-[#AE8625] bg-white shadow-[0px_8px_5px_-5px] shadow-black/10';

        // Use transform for more efficient positioning instead of left
        gsap.set(dot, {
            x: data.position,
            xPercent: -50, // Center the dot on its position
            willChange: 'transform'
        });

        // Clean up will-change after positioning
        gsap.delayedCall(1, () => {
            gsap.set(dot, { willChange: 'auto' });
        });

        const inner = document.createElement('span');
        inner.className = 'block size-[20px] rounded-full bg-[#D2AC47] opacity-0 transition-opacity';
        dot.appendChild(inner);

        dot.addEventListener('click', () => swiper.slideTo(data.index));
        dotsContainer.appendChild(dot);
        dots.push({ element: dot, data });
    });

    const swiper = new Swiper(swiperEl, {
        modules: [Navigation, Parallax],
        slidesPerView: 1,
        speed: 1000,
        parallax: true,
        autoHeight: true,
        navigation: {
            prevEl: activeButtons.prevBtn,
            nextEl: activeButtons.nextBtn,
        },
        allowTouchMove: true,
    });

    // Track slide direction for label animation
    let lastSlideIndex = 0;
    let updateDelayedCall = null; // For debouncing

    function updateUI() {
        const index = swiper.realIndex;
        const total = swiper.slides.length;

        // Use cached data instead of DOM queries
        const slideData = slideDataCache.find(d => d.index === index);
        if (!slideData) return;

        // Update counter based on current screen size
        const currentButtons = window.innerWidth < 1025 ? mobileButtons : desktopButtons;
        if (currentButtons.counterEl) currentButtons.counterEl.textContent = `${index + 1} of ${total}`;

        if (labelEl) {
            const { title, year, date } = slideData;
            const titleEl = labelEl.querySelector('[data-role="timeline-label-text"]');
            const dateEl = labelEl.querySelector('[data-role="timeline-label-date"]');

            // Determine direction: forward (index > lastSlideIndex) or backward (index < lastSlideIndex)
            const isForward = index > lastSlideIndex;
            const fadeOutX = isForward ? -30 : 30;  // fade out left if forward, right if backward
            const fadeInX = isForward ? 30 : -30;   // fade in from right if forward, left if backward

            // Set will-change for label animation
            gsap.set(labelEl, {
                willChange: 'transform, opacity'
            });

            // Animate label transition based on direction (transform + opacity - needs GPU)
            gsap.to(labelEl, {
                x: fadeOutX,
                opacity: 0,
                duration: 0.5,
                ease: "power1.inOut",
                force3D: true,
                onComplete: () => {
                    // Update content when invisible using cached data
                    if (titleEl) titleEl.textContent = title;
                    if (dateEl) {
                        if (date) {
                            dateEl.textContent = date + ", " + year;
                            dateEl.style.display = 'block';
                        } else {
                            dateEl.style.display = 'none';
                        }
                    }

                    // Reset position and fade in from opposite direction (transform + opacity - needs GPU)
                    gsap.set(labelEl, { x: fadeInX });
                    gsap.to(labelEl, {
                        x: 0,
                        opacity: 1,
                        duration: 0.5,
                        ease: "power1.inOut",
                        force3D: true,
                        onComplete: () => {
                            // Clean up will-change after animation
                            gsap.set(labelEl, {
                                willChange: 'auto'
                            });
                        }
                    });
                }
            });
        }

        // Update last slide index for next direction calculation
        lastSlideIndex = index;

        // Find the current active dot based on slide index using cached data
        const activeDotData = dotPositions.find(d => d.index === index);
        if (activeDotData) {
            // Check if we're in mobile mode
            const isCurrentlyMobile = window.innerWidth < 1025; // lg breakpoint

            if (isCurrentlyMobile) {
                // Mobile: Move dots container to show active dot from the left
                const targetPosition = Math.max(-activeDotData.position, -(maxPosition - dotsContainer.parentElement.offsetWidth));

                // Animate the dots container position
                gsap.to(dotsContainer, {
                    x: targetPosition,
                    duration: 1,
                    ease: "power2.inOut",
                    force3D: true
                });
            } else {
                // Desktop: Original transform animation
                gsap.to(dotsContainer, {
                    x: -activeDotData.position,
                    duration: 1,
                    ease: "power2.inOut",
                    force3D: true
                });
            }
        }

        // Update dot active states based on the current slide index
        dots.forEach((dotItem) => {
            const inner = dotItem.element.querySelector('span');
            if (!inner) return;
            if (dotItem.data.index === index) {
                inner.classList.add('opacity-100');
            } else {
                inner.classList.remove('opacity-100');
            }
        });
    }

    // Debounced update function to prevent excessive calls
    function debouncedUpdate() {
        // Cancel any pending update
        if (updateDelayedCall) {
            updateDelayedCall.kill();
        }

        // Schedule new update with debouncing
        updateDelayedCall = gsap.delayedCall(0.1, updateUI);
    }

    updateUI();
    swiper.on('slideChange', debouncedUpdate);

    // Handle resize events for responsive behavior
    let resizeTimeout;
    let currentActiveButtons = activeButtons; // Track current active buttons

    window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
            // Recalculate dots container behavior on resize
            const isResizeMobile = window.innerWidth < 1025;
            const newActiveButtons = isResizeMobile ? mobileButtons : desktopButtons;

            // Check if we need to switch button sets
            if (currentActiveButtons !== newActiveButtons) {
                // Destroy current navigation and reinitialize with new buttons
                swiper.navigation.destroy();
                swiper.params.navigation.prevEl = newActiveButtons.prevBtn;
                swiper.params.navigation.nextEl = newActiveButtons.nextBtn;
                swiper.navigation.init();
                swiper.navigation.update();
                currentActiveButtons = newActiveButtons;
            }

            if (isResizeMobile) {
                dotsContainer.style.minWidth = `${maxPosition + 100}px`;
                gsap.set(dotsContainer, { x: 0 }); // Reset transform position
            } else {
                dotsContainer.style.minWidth = '';
                gsap.set(dotsContainer, { x: 0 }); // Reset transform
            }

            // Update UI to ensure proper positioning
            debouncedUpdate();
        }, 150);
    });
}

// Infinite marquee animation for Dixon Stories cards
function initMarquee() {
    // Intersection Observer for performance - pause off-screen marquees
    const marqueeObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            const timeline = entry.target.marqueeTimeline;
            entry.target._isInView = entry.isIntersecting;
            if (timeline) {
                if (entry.target._isDragging || entry.target._isWheelScrolling) {
                    return;
                }

                const isReversed = entry.target.dataset.reversed === 'true';
                const baseScale = isReversed ? -1 : 1;
                const shouldPause = entry.target._isHoverPaused && !entry.target._isArrowAccelerating;

                if (entry.isIntersecting) {
                    if (entry.target._isArrowAccelerating) {
                        return;
                    }
                    // Resume animation when in view unless user is hovering without actively accelerating
                    timeline.timeScale(shouldPause ? 0 : baseScale);
                } else {
                    // Pause when out of view
                    timeline.timeScale(0);
                }
            }
        });
    }, {
        rootMargin: '100px' // Start animating slightly before entering viewport
    });

    // Initialize the marquee with horizontalLoop using cached elements
    elements.marquee.rows.forEach((line, i) => {
        const cards = line.querySelectorAll('[data-marquee="card"]');
        const wrapper = line.closest('[data-marquee="row-wrapper"]');
        const prevButton = wrapper ? wrapper.querySelector('[data-marquee-control="prev"]') : null;
        const nextButton = wrapper ? wrapper.querySelector('[data-marquee-control="next"]') : null;

        // Alternate direction: even rows (0,2,4...) go left, odd rows (1,3,5...) go right
        const isReversed = i % 2 === 1;
        line.dataset.reversed = isReversed; // Store for observer

        const tl = horizontalLoop(cards, {
            repeat: -1,
            speed: 2,
            reversed: isReversed,
            paddingRight: window.innerWidth >= 1024 ? "40px" : "20px"
        });

        // Store timeline reference for intersection observer
        line.marqueeTimeline = tl;
        line._isInView = false;
        line._isDragging = false;
        line._isWheelScrolling = false;
        line._isHoverPaused = false;
        line._isArrowAccelerating = false;
        line._isHovering = false;
        line._activeArrowDirection = null;

        const pixelsPerSecond = tl.pixelsPerSecond || 50;
        const wrapTime = tl.getWrappedTime || ((time) => time);
        const baseTimeScale = tl.baseTimeScale ?? (isReversed ? -1 : 1);

        const getBaseScale = () => (line._isInView ? baseTimeScale : 0);

        let wheelTimeout;

        const pauseTimeline = () => {
            gsap.killTweensOf(tl, 'timeScale');
            tl.timeScale(0);
        };

        const resumeTimelineToBase = () => {
            if (line._isDragging || line._isWheelScrolling || line._isHoverPaused || line._isArrowAccelerating) {
                return;
            }
            const targetScale = getBaseScale();
            gsap.killTweensOf(tl, 'timeScale');
            gsap.to(tl, {
                timeScale: targetScale,
                duration: 0.5,
                ease: 'power3.out'
            });
        };

        let isDragging = false;
        let lastClientX = 0;
        let lastMoveTime = 0;
        let dragVelocity = 0;
        let dragDistance = 0;
        let dragMoved = false;

        const momentumLimit = 3;
        const dragThreshold = 5; // pixels before we treat interaction as a drag

        const convertWheelDeltaToPixels = (delta, deltaMode) => {
            switch (deltaMode) {
                case 1: // DOM_DELTA_LINE
                    return delta * 16;
                case 2: // DOM_DELTA_PAGE
                    return delta * window.innerHeight;
                default:
                    return delta;
            }
        };

        const handleWheel = (event) => {
            if (event.ctrlKey) {
                return;
            }

            const absDeltaX = Math.abs(event.deltaX);
            const absDeltaY = Math.abs(event.deltaY);

            let delta = event.deltaX;
            if (absDeltaY > absDeltaX) {
                if (event.shiftKey) {
                    delta = event.deltaY;
                } else {
                    return;
                }
            }

            if (delta === 0) {
                return;
            }

            const pixelDelta = convertWheelDeltaToPixels(delta, event.deltaMode);
            if (pixelDelta === 0) {
                return;
            }

            event.preventDefault();

            line._isWheelScrolling = true;
            pauseTimeline();

            const deltaTime = (pixelDelta * -1) / pixelsPerSecond;
            const nextTime = wrapTime(tl.totalTime() - deltaTime);
            tl.totalTime(nextTime);

            clearTimeout(wheelTimeout);
            wheelTimeout = setTimeout(() => {
                line._isWheelScrolling = false;
                line._isHoverPaused = false;
                resumeTimelineToBase();
            }, 150);
        };

        const handlePointerDown = (event) => {
            if (event.button !== undefined && event.button !== 0) {
                return;
            }
            clearTimeout(wheelTimeout);
            line._isWheelScrolling = false;
            isDragging = true;
            line._isDragging = true;
            line.style.cursor = 'ew-resize';
            lastClientX = event.clientX;
            lastMoveTime = performance.now();
            dragVelocity = 0;
            dragDistance = 0;
            dragMoved = false;
            line._suppressClick = false;
            line._isHoverPaused = false;
            pauseTimeline();
            const isTouchLikePointer = event.pointerType === 'touch' || event.pointerType === 'pen';
            if (isTouchLikePointer && event.pointerId !== undefined && line.setPointerCapture) {
                try {
                    line.setPointerCapture(event.pointerId);
                } catch (err) {
                    // Ignore errors if pointer capture is not available
                }
            }
            if (isTouchLikePointer) {
                event.preventDefault();
            }
        };

        const handlePointerMove = (event) => {
            if (!isDragging) {
                return;
            }

            const clientX = event.clientX;
            const deltaX = clientX - lastClientX;

            if (deltaX !== 0) {
                const deltaTime = deltaX / pixelsPerSecond;
                const nextTime = wrapTime(tl.totalTime() - deltaTime);
                tl.totalTime(nextTime);

                const now = performance.now();
                const elapsed = now - lastMoveTime;
                if (elapsed > 0) {
                    dragVelocity = deltaX / elapsed; // px per ms
                }
                lastMoveTime = now;
                lastClientX = clientX;
                dragDistance += Math.abs(deltaX);
                if (!dragMoved && dragDistance >= dragThreshold) {
                    dragMoved = true;
                }
            }

            event.preventDefault();
        };

        const resumeTimelineWithMomentum = () => {
            if (line._isWheelScrolling) {
                return;
            }
            line._isHoverPaused = false;
            const baseScale = getBaseScale();
            const velocityPxPerSec = dragVelocity * 1000; // convert to px/s
            const velocityScale = pixelsPerSecond ? -velocityPxPerSec / pixelsPerSecond : 0;
            const clampedScale = gsap.utils.clamp(-momentumLimit, momentumLimit, velocityScale);

            gsap.killTweensOf(tl, 'timeScale');

            if (Math.abs(clampedScale) > Math.abs(baseScale) && line._isInView) {
                gsap.to(tl, {
                    timeScale: clampedScale,
                    duration: 0.15,
                    overwrite: true,
                    onComplete: () => {
                        gsap.to(tl, {
                            timeScale: baseScale,
                            duration: 0.8,
                            ease: 'power3.out'
                        });
                    }
                });
            } else {
                gsap.to(tl, {
                    timeScale: baseScale,
                    duration: 0.5,
                    ease: 'power3.out'
                });
            }
        };

        const handlePointerUp = (event) => {
            if (!isDragging) {
                return;
            }

            isDragging = false;
            line._isDragging = false;
            line.style.cursor = 'ew-resize';

            if (event && event.pointerId !== undefined && line.releasePointerCapture) {
                try {
                    line.releasePointerCapture(event.pointerId);
                } catch (err) {
                    // Ignore release errors
                }
            }

            resumeTimelineWithMomentum();
            dragVelocity = 0;

            if (dragMoved) {
                line._suppressClick = true;
                setTimeout(() => {
                    line._suppressClick = false;
                }, 100);
            }
        };

        line.addEventListener('pointerdown', handlePointerDown, { passive: false });
        line.addEventListener('pointermove', handlePointerMove, { passive: false });
        line.addEventListener('pointerup', handlePointerUp);
        line.addEventListener('pointercancel', handlePointerUp);
        line.addEventListener('pointerleave', handlePointerUp);
        line.addEventListener('wheel', handleWheel, { passive: false });

        if (wrapper) {
            wrapper.addEventListener('mouseenter', () => {
                line._isHovering = true;
                if (line._isDragging || line._isWheelScrolling || line._isArrowAccelerating) {
                    return;
                }
                line._isHoverPaused = true;
                pauseTimeline();
            });

            wrapper.addEventListener('mouseleave', () => {
                line._isHovering = false;
                if (line._isArrowAccelerating) {
                    return;
                }
                line._isHoverPaused = false;
                resumeTimelineToBase();
            });
        }

        line.style.cursor = 'ew-resize';
        if (!line.style.touchAction) {
            line.style.touchAction = 'pan-y';
        }

        // Observe this marquee row for intersection
        marqueeObserver.observe(line);

        const prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const arrowAccelerationFactor = prefersReducedMotion ? 1.5 : 25;
        const getArrowScale = (direction) => {
            return direction === 'forward' ? -arrowAccelerationFactor : arrowAccelerationFactor;
        };

        const startArrowAcceleration = (direction) => {
            if (line._isDragging || line._isWheelScrolling) {
                return;
            }
            if (line._isArrowAccelerating && line._activeArrowDirection === direction) {
                return;
            }
            line._isArrowAccelerating = true;
            line._activeArrowDirection = direction;
            line._isHoverPaused = false;
            const targetScale = getArrowScale(direction);
            gsap.killTweensOf(tl, 'timeScale');
            gsap.to(tl, {
                timeScale: targetScale,
                duration: 0.2,
                ease: 'power2.out'
            });
        };

        const stopArrowAcceleration = () => {
            if (!line._isArrowAccelerating) {
                return;
            }
            line._isArrowAccelerating = false;
            line._activeArrowDirection = null;
            if (line._isHovering) {
                line._isHoverPaused = true;
                pauseTimeline();
            } else {
                line._isHoverPaused = false;
                resumeTimelineToBase();
            }
        };

        const bindArrow = (button, direction) => {
            if (!button) {
                return;
            }

            const handlePointerDown = (event) => {
                if (event.button !== undefined && event.button !== 0) {
                    return;
                }
                event.preventDefault();
                if (event.pointerId !== undefined && button.setPointerCapture) {
                    try {
                        button.setPointerCapture(event.pointerId);
                    } catch (err) {
                        // Ignore capture errors
                    }
                }
                startArrowAcceleration(direction);
            };

            const handlePointerEnd = (event) => {
                if (event && event.pointerId !== undefined && button.releasePointerCapture) {
                    try {
                        button.releasePointerCapture(event.pointerId);
                    } catch (err) {
                        // Ignore release errors
                    }
                }
                stopArrowAcceleration();
            };

            button.addEventListener('pointerdown', handlePointerDown, { passive: false });
            button.addEventListener('pointerup', handlePointerEnd);
            button.addEventListener('pointercancel', handlePointerEnd);
            button.addEventListener('lostpointercapture', stopArrowAcceleration);
            button.addEventListener('blur', stopArrowAcceleration);
            button.addEventListener('pointerleave', (event) => {
                if (!line._isArrowAccelerating) {
                    return;
                }
                if (event.pointerType === 'mouse' && event.buttons === 0) {
                    stopArrowAcceleration();
                }
            });

            button.addEventListener('keydown', (event) => {
                if (event.code !== 'Enter' && event.code !== 'Space') {
                    return;
                }
                event.preventDefault();
                startArrowAcceleration(direction);
            });

            button.addEventListener('keyup', (event) => {
                if (event.code !== 'Enter' && event.code !== 'Space') {
                    return;
                }
                event.preventDefault();
                stopArrowAcceleration();
            });
        };

        bindArrow(nextButton, 'backward');
        bindArrow(prevButton, 'forward');

        // Simple hover interactions for individual cards
        cards.forEach(card => {
            card.addEventListener('mouseenter', () => {
                if (isDragging || line._isArrowAccelerating) {
                    return;
                }
                line._isHoverPaused = true;
                pauseTimeline();
            });
            card.addEventListener('mouseleave', () => {
                if (isDragging || line._isArrowAccelerating) {
                    return;
                }
                if (!line._isHovering) {
                    line._isHoverPaused = false;
                    resumeTimelineToBase();
                } else {
                    line._isHoverPaused = true;
                }
            });
            card.addEventListener('click', (event) => {
                if (line._suppressClick) {
                    event.preventDefault();
                    event.stopImmediatePropagation();
                }
            }, true);
        });
    });
}


// horizontalLoop helper function
function horizontalLoop(items, config) {
    items = gsap.utils.toArray(items);
    config = config || {};
    let tl = gsap.timeline({repeat: config.repeat, paused: config.paused, defaults: {ease: "none"}, onReverseComplete: () => tl.totalTime(tl.rawTime() + tl.duration() * 100)}),
        length = items.length,
        startX = items[0].offsetLeft,
        times = [],
        widths = [],
        xPercents = [],
        curIndex = 0,
        pixelsPerSecond = (config.speed || 1) * 25,
        snap = config.snap === false ? v => v : gsap.utils.snap(config.snap || 1),
        totalWidth, curX, distanceToStart, distanceToLoop, item, i;

    gsap.set(items, {
        xPercent: (i, el) => {
            let w = widths[i] = parseFloat(gsap.getProperty(el, "width", "px"));
            xPercents[i] = snap(parseFloat(gsap.getProperty(el, "x", "px")) / w * 100 + gsap.getProperty(el, "xPercent"));
            return xPercents[i];
        }
    });

    gsap.set(items, {x: 0});
    totalWidth = items[length-1].offsetLeft + xPercents[length-1] / 100 * widths[length-1] - startX + items[length-1].offsetWidth * gsap.getProperty(items[length-1], "scaleX") + (parseFloat(config.paddingRight) || 0);

    for (i = 0; i < length; i++) {
        item = items[i];
        curX = xPercents[i] / 100 * widths[i];
        distanceToStart = item.offsetLeft + curX - startX;
        distanceToLoop = distanceToStart + widths[i] * gsap.getProperty(item, "scaleX");
        tl.to(item, {xPercent: snap((curX - distanceToLoop) / widths[i] * 100), duration: distanceToLoop / pixelsPerSecond}, 0)
          .fromTo(item, {xPercent: snap((curX - distanceToLoop + totalWidth) / widths[i] * 100)}, {xPercent: xPercents[i], duration: (curX - distanceToLoop + totalWidth - curX) / pixelsPerSecond, immediateRender: false}, distanceToLoop / pixelsPerSecond)
          .add("label" + i, distanceToStart / pixelsPerSecond);
        times[i] = distanceToStart / pixelsPerSecond;
    }

    tl.pixelsPerSecond = pixelsPerSecond;
    tl.baseTimeScale = config.reversed ? -1 : 1;
    tl.getWrappedTime = (time) => {
        const duration = tl.duration();
        if (!duration) {
            return time;
        }
        return ((time % duration) + duration) % duration;
    };

    function toIndex(index, vars) {
        vars = vars || {};
        (Math.abs(index - curIndex) > length / 2) && (index += index > curIndex ? -length : length);
        let newIndex = gsap.utils.wrap(0, length, index),
            time = times[newIndex];
        if (time > tl.time() !== index > curIndex) {
            vars.modifiers = {time: gsap.utils.wrap(0, tl.duration())};
            time += tl.duration() * (index > curIndex ? 1 : -1);
        }
        curIndex = newIndex;
        vars.overwrite = true;
        return tl.tweenTo(time, vars);
    }

    tl.next = vars => toIndex(curIndex+1, vars);
    tl.previous = vars => toIndex(curIndex-1, vars);
    tl.current = () => curIndex;
    tl.toIndex = (index, vars) => toIndex(index, vars);
    tl.times = times;

    if (config.reversed) {
        tl.vars.onReverseComplete();
        tl.reverse();
    }

    return tl;
}
